<template>
  <div class="note-item">
    <div class="note-item__container">
      <div v-if="!isNoteEditActive">
        <wa-avatar style="--size: 28px" :initials="getInitialsFromFullName(note.name)" />
      </div>
      <div class="note-item__wrapper">
        <div v-if="!isNoteEditActive" class="note-item__creator-info">
          <div>
            <p v-if="note.customer_note">
              {{ $t('note_item.customer_note.title') }}
            </p>
            <p v-else class="text-size-small text-color-black font-weight-semi-bold">
              {{ note.name }}, {{ note.reference }}
            </p>
            <p class="text-size-small text-color-neutral font-weight-semi-bold">
              {{ formatISODateToDDMMYYYYWithTime(note.updated_at) }}
            </p>
          </div>
          <ThreeDotsMenu placement="bottom-end">
            <template #menu>
              <wa-dropdown-item :disabled="!props.canEdit" @click="setNoteEditActive">
                <PhPencilSimpleLine :size="20" /> {{ $t('globals.edit') }}
              </wa-dropdown-item>
              <wa-dropdown-item @click="showDeleteNoteDialog = true">
                <PhTrash :size="20" /> {{ $t('globals.delete') }}
              </wa-dropdown-item>
            </template>
          </ThreeDotsMenu>
        </div>
        <div v-if="isNoteEditActive" ref="textAreaFormWrapper" class="note-item__textarea-wrapper">
          <FormTextArea
            v-model="noteDescription"
            :class="{ 'note-textarea-inactive': !isNoteEditActive }"
            :inert="!isNoteEditActive"
            resize="none"
            rows="5"
            :maxlength="isNoteEditActive ? 300 : 0"
          />
        </div>
        <div v-else class="note-content">
          <div style="display: flex; flex-direction: column; gap: 16px">
            <div v-if="hasExtraInfo">
              <wa-button
                variant="neutral"
                appearance="outlined"
                v-if="note.metadata?.prescription?.date"
                @click="uiStore.$patch({ currentCustomerDrawer: 'prescriptions' })"
              >
                {{
                  $t('note_item.buttons.prescription', {
                    date: formatISODateToDDMMYYYY(note.metadata.prescription.date)
                  })
                }}
              </wa-button>
              <div
                v-else-if="note.attachable?.type === 'orders'"
                style="display: flex; gap: 8px; align-items: center"
              >
                <i18n-t tag="p" keypath="note_item.buttons.order">
                  <template #number>
                    <span class="text-underline link" @click="openOrder(note.attachable.id)">
                      #{{ note.attachable.number }}
                    </span>
                  </template>
                </i18n-t>
                <wa-tag
                  v-if="note.attachable.status === 'cancelled'"
                  size="s"
                  variant="primary-orange"
                  >{{ $t('afterSales.status.cancelled') }}</wa-tag
                >
                <wa-tag
                  v-else-if="note.attachable.fulfillment_status"
                  :key="note.attachable.fulfillment_status"
                  size="s"
                  :variant="getTagVariantForFulfilmentStatus(note.attachable.fulfillment_status)"
                >
                  {{
                    capitalizeFirstLetter(note.attachable.fulfillment_status?.split('_').join(' '))
                  }}</wa-tag
                >
              </div>
              <Appointment v-else-if="timifyAppointment" :appointment="timifyAppointment" />
            </div>
            <p class="text-size-medium text-color-black">{{ noteDescription }}</p>
          </div>
        </div>
        <wa-button
          v-if="isNoteEditActive"
          style="width: max-content; margin-bottom: 24px"
          variant="brand"
          @click="handleNotesUpdate"
        >
          {{ $t('globals.confirm') }}</wa-button
        >
      </div>
    </div>

    <wa-dialog
      light-dismiss
      class="promptDialog"
      :open="showDeleteNoteDialog"
      :label="$t('order.dialog.delete_note.title')"
      @wa-request-close="handleCloseDialog"
    >
      <p>{{ $t('order.dialog.delete_note.description') }}</p>
      <wa-button
        slot="footer"
        variant="neutral"
        appearance="outlined"
        @click="handleCloseDialog()"
        >{{ $t('globals.cancel') }}</wa-button
      >
      <wa-button ref="saveButton" slot="footer" variant="brand" @click="handleNoteDelete">{{
        $t('globals.yes_delete')
      }}</wa-button>
    </wa-dialog>
  </div>
</template>

<script setup>
import '@awesome.me/webawesome/dist/components/button/button.js'

import Appointment from './Appointment/Appointment.vue'

import { ref, inject, computed } from 'vue'
import { getErrorMessage } from '@/utils/error.js'
import { useNotification } from '@/composables/useNotification.js'
import FormTextArea from '@/components/Form/FormTextArea/FormTextArea.vue'
import '@awesome.me/webawesome/dist/components/avatar/avatar.js'
import { useDeviceStore } from '@/stores/device.js'
import { formatISODateToDDMMYYYYWithTime, formatISODateToDDMMYYYY } from '@/utils/dateTime.js'
import ThreeDotsMenu from '@/components/shared/ThreeDotsMenu/ThreeDotsMenu.vue'
import { PhPencilSimpleLine, PhTrash } from '@phosphor-icons/vue'
import { onClickOutside } from '@vueuse/core'
import { getInitialsFromFullName, capitalizeFirstLetter } from '@/utils/string.js'
import { useUiStore } from '@/stores/ui.js'
import { useCustomerStore } from '@/stores/customer.js'
import { getTagVariantForFulfilmentStatus } from '@/utils/status.js'

const { showNotification } = useNotification()
const deviceStore = useDeviceStore()
const uiStore = useUiStore()
const customerStore = useCustomerStore()

const cl = inject('cl')
const emit = defineEmits(['fetchNotes'])
const props = defineProps({
  note: {
    type: Object,
    default: () => {}
  },
  canEdit: {
    type: Boolean,
    default: true
  },
  showExtraInfo: {
    type: Boolean,
    default: false
  }
})

const noteBackup = ref('')
const noteDescription = ref(props.note.description)
const isNoteEditActive = ref(false)
const isUpdateClicked = ref(false)
const showDeleteNoteDialog = ref(false)
const textAreaFormWrapper = ref()
const currentEmployee = computed(() => deviceStore.settings.currentEmployee)
const timifyAppointment = computed(() => {
  return props.note?.metadata?.timify_appointment
})
const hasExtraInfo = computed(() => {
  return (
    props.showExtraInfo &&
    Boolean(
      props.note.metadata?.prescription?.date ||
        props.note.attachable?.type === 'orders' ||
        timifyAppointment.value
    )
  )
})

async function handleNotesUpdate() {
  isUpdateClicked.value = true
  try {
    if (noteDescription.value === props.note.description) {
      isNoteEditActive.value = false
      return
    }

    const response = await cl.attachments.update({
      id: props.note.id,
      description: noteDescription.value,
      metadata: {
        ...props.note.metadata,
        employee_updated: {
          eid: currentEmployee.value.number,
          name: currentEmployee.value.name
        }
      }
    })

    if (response) {
      emit('fetchNotes')
    }
    isNoteEditActive.value = false
    isUpdateClicked.value = false
  } catch (error) {
    showNotification({
      message: getErrorMessage(error),
      variant: 'danger'
    })
  }
}

async function handleNoteDelete() {
  try {
    await cl.attachments.update({
      id: props.note.id,
      reference: 'app-orders--deleted-note'
    })
    emit('fetchNotes')
  } catch (error) {
    showNotification({
      message: getErrorMessage(error),
      variant: 'danger'
    })
  }
}

function handleCloseDialog(event) {
  if (event) {
    event.stopPropagation()
  }
  showDeleteNoteDialog.value = false
}

function setNoteEditActive() {
  if (props.canEdit) {
    noteBackup.value = noteDescription.value
    isNoteEditActive.value = true
  }
}

onClickOutside(textAreaFormWrapper, (event) => {
  const eventTarget = event.target
  if (eventTarget.nodeName === 'SL-BUTTON') {
    isNoteEditActive.value = false
  } else {
    // if note hasn't been updated, and it's clicked outside, reset the value
    if (noteBackup.value !== noteDescription.value) {
      noteDescription.value = noteBackup.value
    }
    isNoteEditActive.value = false
  }
})

function openOrder(id) {
  customerStore.setCustomerOrderId(id)
  uiStore.showCustomerOrderDetails = true
}
</script>

<style lang="postcss" scoped src="./NoteItem.css" />
